import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/definition"
import {ImageOpBlitHal} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/hal"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {BasicSpecializingImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/basic-specializing-image-op-node"

const nodeDefinition = {...Definition, nodeClass: "ImageOpNodeBlit"}

@registerImageOpNode
export class ImageOpNodeBlit extends BasicSpecializingImageOpNode<typeof Definition, ImageOpBlitHal> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof Definition>) {
        super(ImageOpBlitHal, parameters)
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof Definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        if (evaluatedParameters.outOfBoundsHandling !== "clip") {
            throw new Error(`Blit out of bounds mode ${evaluatedParameters.outOfBoundsHandling} is not supported in imgproc`)
        }
        const descriptor = evaluatedParameters.backgroundImage.descriptor
        const foregroundRegion = evaluatedParameters.foregroundRegion ?? {
            x: 0,
            y: 0,
            width: evaluatedParameters.foregroundImage.descriptor.width,
            height: evaluatedParameters.foregroundImage.descriptor.height,
        }
        const lowBoundOverhang = {
            x: Math.max(0, -evaluatedParameters.position.x),
            y: Math.max(0, -evaluatedParameters.position.y),
        }
        const highBoundOverhang = {
            x: Math.max(0, evaluatedParameters.position.x + foregroundRegion.width - descriptor.width),
            y: Math.max(0, evaluatedParameters.position.y + foregroundRegion.height - descriptor.height),
        }
        const clippedTargetPosition = {
            x: evaluatedParameters.position.x + lowBoundOverhang.x,
            y: evaluatedParameters.position.y + lowBoundOverhang.y,
        }
        foregroundRegion.x += lowBoundOverhang.x
        foregroundRegion.y += lowBoundOverhang.y
        foregroundRegion.width -= lowBoundOverhang.x + highBoundOverhang.x
        foregroundRegion.height -= lowBoundOverhang.y + highBoundOverhang.y
        // In the common case we must not mutate backgroundImage: it may still be needed elsewhere in the graph.
        // Callers that can prove backgroundImage is otherwise unused (e.g. clone-stamp's stroke chain) opt in via
        // `inPlace`, which skips the full-canvas allocation + copy and pastes the foreground directly into it.
        const backgroundNode = evaluatedParameters.inPlace
            ? evaluatedParameters.backgroundImage.node
            : createImageOpImgProc(descriptor, {
                  type: "copyRegion",
                  source: evaluatedParameters.backgroundImage.node,
                  target: evaluator.context.createEmptyImage(descriptor).node,
              }).node
        return createImageOpImgProc(descriptor, {
            type: "copyRegion",
            source: evaluatedParameters.foregroundImage.node,
            sourceRegion: {
                type: "region",
                region: [foregroundRegion.x, foregroundRegion.y, foregroundRegion.width, foregroundRegion.height],
            },
            target: backgroundNode,
            targetOffset: {
                type: "offset",
                offset: [clippedTargetPosition.x, clippedTargetPosition.y],
            },
        })
    }

    // private halOutput: ImageOpBlitHal | null = null
}
